import React from 'react';
import Cookies from 'js-cookie';
import qs from 'qs';
import 'twin.macro';
import { UndoRedo } from 'containers/CahierJournal/Toolbar/UndoRedo';
import { format } from 'utils/time/format';
import Button from 'components/Buttons/Button';
import Tooltip2 from 'components/Tooltip2';
import { Menu } from 'components/Menu';
import {
  ArrowsPointingOutIcon,
  EllipsisHorizontalIcon as DotsHorizontal,
  ServerStackIcon as ServerStack,
} from '@heroicons/react/24/outline';
import tw from 'twin.macro';
import {
  frenchDatePattern,
  isoDatePatternWithSlashes,
} from 'utils/time/datePatterns';
import { useLocation } from 'react-router-dom';
import { useUser } from 'containers/App/context/useUser';
import { useOpenCJSendToCMSModal } from 'containers/CahierJournal/CJSendToCMS/useOpenUpdateCJSeanceModal';
import { useTranslation } from 'react-i18next';
import { CahierJournalLink } from 'containers/CahierJournal/CahierJournalLink';
import { Filters } from 'containers/CahierJournal/Toolbar/Filters';
import { DownloadOrPreviewPDFDays } from 'containers/CahierJournal/DownloadOrPreviewPDFDay';
import { useDownloadSexyCJ } from 'containers/CahierJournal/queries/useDownloadSexyCJ';
import { CahierJournalButtonLink } from 'containers/CahierJournal/CahierJournalButtonLink';
import { SingleDayMenuItems } from './SingleDayMenuItems';

interface Props {
  day: Date;
  view: string;
  isCollapsed?: boolean;
  setCollapse: (isCollapsed: boolean) => void;
  className?: string;
  previewLink: string;
  disabled?: boolean;
}

export function Header(props: Props) {
  const {
    fonts: { cj: settings },
  } = useUser();
  const { isDownloading, download } = useDownloadSexyCJ();
  const location = useLocation();
  const { locale, guestMode } = useUser();
  const isSharable =
    qs.parse(location.search.substr(1))?.sharable || Cookies.get('teetshadmin');
  const isOnDashboard = location.pathname.endsWith('/dashboard');

  const { day, view, previewLink, disabled } = props;
  const user = useUser();
  const openCJSendToCMSModal = useOpenCJSendToCMSModal();
  const { t } = useTranslation(['cahierJournal', 'common']);

  const downloadCJ = () =>
    download({
      day: format(day, 'fr-FR'),
      view: 'day',
      direction: 'next',
      settings,
      lng: user.locale,
      tz: user.location,
    });

  const dayLink = `cahier-journal/day/${format(
    day,
    'fr-FR',
    isoDatePatternWithSlashes,
  )}`;
  return (
    <div
      css={[tw`flex relative shrink-0`, view !== 'day' && tw`px-2`]}
      className={props.className}
    >
      {view !== 'day' ? (
        <CahierJournalLink
          to={dayLink}
          tw="text-neutral-moderate mr-2"
          readonly={disabled}
        >
          <h2>
            {format(day, locale, 'eee')}
            <br />
            {format(day, locale, 'dd')}
          </h2>
        </CahierJournalLink>
      ) : (
        <div tw="mt-4" />
      )}
      <div tw="flex items-center ml-1 gap-x-1">
        <CahierJournalButtonLink
          minimal
          large
          icon={ArrowsPointingOutIcon}
          to={dayLink}
          disabled={disabled}
        />
        <DownloadOrPreviewPDFDays
          onQuickDownload={downloadCJ}
          isDownloading={isDownloading}
          previewLink={previewLink}
          disabled={disabled}
        />
        <Tooltip2 label={t('cahierJournal:dayHeader.displaySettings')}>
          <Filters css={[!isOnDashboard && tw`md:hidden`]} />
        </Tooltip2>
        {isSharable && !guestMode?.enabled && (
          <Tooltip2 label={t('common:buttons.sendToCMS')}>
            <Button
              id="edt-share-publicly"
              minimal
              large
              onClick={() =>
                openCJSendToCMSModal({
                  date: day,
                })
              }
              icon={ServerStack}
            />
          </Tooltip2>
        )}
        {guestMode?.permission !== 'read' && (
          <Menu portal>
            <Tooltip2
              label={t('cahierJournal:dayHeader.actionsOnTheDay')}
              disabled={disabled}
            >
              <Menu.Button
                minimal
                large
                disabled={disabled}
                aria-label={`Préférences journée ${format(
                  day,
                  'fr-FR',
                  frenchDatePattern,
                )}`}
                icon={DotsHorizontal}
              />
            </Tooltip2>
            <Menu.Items size="auto">
              <SingleDayMenuItems
                previewLink={previewLink}
                view={view}
                day={day}
              />
            </Menu.Items>
          </Menu>
        )}
      </div>
      <div tw="md:hidden ml-auto">
        <UndoRedo minimal />
      </div>
    </div>
  );
}
